// /affordable — Affordable Outdoor Property Management (Ohio)
// Lead Architect: Elvis. Phone: 419-529-2002.
// Skin: Earth & Stone — deep forest green, slate grey, granite
// cream. Property-management authority — rugged, load-bearing,
// professional. Reads like a boots-on-gravel command post.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Phone,
  Sunrise,
  Wallet,
  ClipboardList,
  Megaphone,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import VerticalCockpit from "@/components/VerticalCockpit";
import { PALETTES, LINKS, COPY } from "@/config/AssetSpine";
import { VERTICAL_CONFIG } from "@/config/verticalConfig";

const P = PALETTES.AFFORDABLE;

// Scaling Blueprint · five challenges that kill Ohio property
// management growth. Written for a founder who runs crews and
// knows the ground rules — no marketing fluff.
const BLUEPRINT_CHALLENGES = [
  {
    n: 1,
    icon: Phone,
    title: "The Invisible Phone",
    subtitle: "Lead Leakage",
    body:
      "Every unanswered ring is a portfolio owner shopping their next competitor. In outdoor property management, an inquiry lost at 7:12pm on a Tuesday doesn't reschedule — it lands on the next name in a Google search. Founders who scale past three crews stop letting the phone ring. Amanda answers every line, logs the property address, captures the scope, and books the walkthrough — before the caller has a chance to think about calling anyone else.",
  },
  {
    n: 2,
    icon: Wallet,
    title: "The Sunday Paperwork Trap",
    subtitle: "Billing Lag",
    body:
      "You mow six days a week and bill on the seventh. Recurring invoices sit in a spreadsheet, late fees never trigger, and by the end of the quarter you're carrying four figures in owed receivables per crew. Founders who scale automate the invoice the second the work order closes. Amanda posts the recurring bill, texts the owner a pay-link, and files the statement — the same afternoon the crew rolls off the property.",
  },
  {
    n: 3,
    icon: Sunrise,
    title: "Crew Chaos",
    subtitle: "Dispatch Friction",
    body:
      "Two crews. Nine properties. One text thread. Someone forgets the gate combo, another crew skips the hedge line, and by Thursday you're personally driving out to fix what should've been on the work order. Founders who scale build a single dispatch board every crew opens at 6:30am — property address, scope, gate codes, access notes, all locked in the night before. No group texts. No missed stops.",
  },
  {
    n: 4,
    icon: ClipboardList,
    title: "Fragmented Memory",
    subtitle: "Missing Client Notes",
    body:
      "The tenant said the sprinkler was busted. The owner asked to move the trash pull to Fridays. The crew found a downspout blockage that needs scoping next visit. Three notes, three heads, three lost details — until the owner calls in March asking why nothing was done. Founders who scale run a Property Vault. Every note is voice-to-text logged against the property file the moment it's made. Search 'gate combo' or 'sprinkler shutoff' — the answer is on the truck screen in seconds.",
  },
  {
    n: 5,
    icon: Megaphone,
    title: "Social Silence",
    subtitle: "Inconsistent Brand Presence",
    body:
      "The best property management operators in Ohio look invisible online. You do 40 turn-overs a year and post twice. Meanwhile the mediocre competitor two towns over posts a mow reel every Tuesday and wins the portfolio you should've had. Founders who scale don't guess at content — Amanda drafts ten cinematic property transformations a week: turn-over reveals, portfolio walk-throughs, owner testimonials, seasonal service pushes. Scheduled, posted, filed.",
  },
];

function BlueprintChallenge({ ch, isOpen, onToggle }) {
  const Icon = ch.icon;
  return (
    <div
      data-testid={`af-challenge-${ch.n}`}
      className="rounded-2xl border overflow-hidden transition-all"
      style={{
        borderColor: `${P.ACCENT}22`,
        background: isOpen ? P.BG_HI : `${P.BG_HI}CC`,
        boxShadow: isOpen
          ? `0 20px 40px -20px ${P.PRIMARY_DEEP}55, inset 0 1px 0 ${P.ACCENT}11`
          : `inset 0 1px 0 ${P.ACCENT}0A`,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`af-challenge-toggle-${ch.n}`}
        className="w-full flex items-center gap-5 px-6 py-5 text-left"
      >
        {/* Numeral · granite plate */}
        <div
          className="flex-shrink-0 w-14 h-14 rounded-xl grid place-items-center font-mono text-xl border"
          style={{
            background: `linear-gradient(180deg, ${P.BG_HI} 0%, ${P.BG} 100%)`,
            borderColor: `${P.ACCENT}33`,
            color: P.ACCENT,
            boxShadow: `inset 0 1px 0 ${P.ACCENT}22`,
          }}
        >
          {String(ch.n).padStart(2, "0")}
        </div>

        {/* Title block */}
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.18em] mb-1 flex items-center gap-2"
            style={{ color: P.MUTED }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: P.PRIMARY }} />
            <span>Challenge {ch.n} · {ch.subtitle}</span>
          </div>
          <div
            className="text-lg sm:text-xl font-semibold leading-tight"
            style={{ color: P.INK }}
          >
            {ch.title}
          </div>
        </div>

        <ChevronDown
          className="w-5 h-5 flex-shrink-0 transition-transform"
          style={{
            color: P.MUTED,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {isOpen && (
        <div className="px-6 pb-6 pl-[92px]">
          <p
            className="text-[15px] leading-[1.7]"
            style={{ color: P.MUTED }}
          >
            {ch.body}
          </p>
        </div>
      )}
    </div>
  );
}

function ScalingBlueprint() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      data-testid="af-scaling-blueprint"
      className="relative border-t"
      style={{
        background: P.BG,
        borderColor: `${P.ACCENT}1F`,
      }}
    >
      {/* Granite texture underlay */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            `radial-gradient(circle at 20% 30%, ${P.ACCENT} 0.5px, transparent 1px),
             radial-gradient(circle at 70% 60%, ${P.ACCENT} 0.5px, transparent 1px),
             radial-gradient(circle at 40% 80%, ${P.PRIMARY} 0.5px, transparent 1px)`,
          backgroundSize: "24px 24px, 32px 32px, 40px 40px",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
        {/* Kicker */}
        <div
          className="text-[11px] uppercase tracking-[0.22em] mb-4 flex items-center gap-3"
          style={{ color: P.PRIMARY }}
        >
          <BookOpen className="w-4 h-4" />
          <span>Introductory E-Book · Free For Elvis</span>
        </div>

        {/* Heading */}
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.08] mb-5"
          style={{ color: P.INK }}
        >
          The Scaling Blueprint:{" "}
          <span
            className="italic font-normal"
            style={{ color: P.ACCENT }}
          >
            5 Challenges that Kill Ohio Property Management Growth.
          </span>
        </h2>

        <p
          className="text-base sm:text-lg leading-relaxed max-w-3xl mb-12"
          style={{ color: P.MUTED }}
        >
          Written for the founder who already runs crews. No fluff, no
          motivation posters — just the five friction points every Ohio
          outdoor property management operator hits between crew three and
          crew ten, and how the Grid removes them.
        </p>

        {/* Challenges */}
        <div className="space-y-4">
          {BLUEPRINT_CHALLENGES.map((ch, i) => (
            <BlueprintChallenge
              key={ch.n}
              ch={ch}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>

        {/* Foot CTA */}
        <div
          className="mt-14 rounded-2xl border p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
          style={{
            background:
              `linear-gradient(135deg, ${P.PRIMARY_DEEP}55 0%, ${P.BG_HI} 55%, ${P.BG} 100%)`,
            borderColor: `${P.PRIMARY}55`,
          }}
        >
          <div className="flex-1">
            <div
              className="text-[10px] uppercase tracking-[0.22em] mb-2"
              style={{ color: P.ACCENT }}
            >
              Founder 1-2-12 Ladder · Locked for Life
            </div>
            <div
              className="text-xl sm:text-2xl font-semibold leading-tight mb-1"
              style={{ color: P.INK }}
            >
              $0 Month 1 · $97 Month 2 · $297/mo Year 1 →
            </div>
            <div className="text-sm" style={{ color: P.MUTED }}>
              Elvis, if you&apos;re on this page, the Founder rate is yours. Rest
              of Year 1 onward, locked. New Founder Nodes after Q1 2026 pay
              the full going rate.
            </div>
          </div>

          <Link
            to="/induction?vertical=affordable"
            data-testid="af-blueprint-cta"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-[15px] whitespace-nowrap transition-transform hover:-translate-y-0.5"
            style={{
              background: P.PRIMARY,
              color: P.INK,
              boxShadow: `0 12px 28px -14px ${P.PRIMARY_DEEP}, inset 0 1px 0 ${P.ACCENT}44`,
            }}
          >
            {COPY.FOUNDER_CTA_LABEL}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Affordable() {
  return (
    <>
      <VerticalCockpit
        config={VERTICAL_CONFIG.AFFORDABLE}
        palette={PALETTES.AFFORDABLE}
        testPrefix="af"
      />
      <ScalingBlueprint />
    </>
  );
}
